class ProductMiniMap extends HTMLElement {
  connectedCallback() {
    this.initMap();
  }

  async initMap() {
    const { Map } = await google.maps.importLibrary("maps");

    this.dark = this.getAttribute("data-scheme") === "dark";
    this.tan = this.getAttribute("data-tone") === "tan";
    this.mapColor = this.getAttribute("data-map-color");

    this.map = new Map(this.querySelector("#mini-map"), {
      zoom: 11,
      backgroundColor: this.dark ? "#1d1d1d" : this.mapColor || (this.tan ? "#ebdacc" : "#eeeeee"),
      center: { lat: 34.052235, lng: -118.243683 },
      disableDefaultUI: true,
      clickableIcons: false,
      gestureHandling: "none",
      styles: this.dark ? this.darkStyles() : this.lightStyles(this.tan),
    });

    this.wrapper = this.closest("[data-pf-map]");
    this.card = this.querySelector("[data-map-card]");
    this.querySelectorAll("[data-map-zoom]").forEach((button) => {
      button.addEventListener("click", () => {
        const step = Number(button.getAttribute("data-map-zoom")) || 0;
        this.userZoomed = true;
        if (this.baseZoom == null) this.baseZoom = this.map.getZoom();
        this.map.setZoom(Math.max(3, Math.min(18, this.map.getZoom() + step)));
      });
    });
    this.markerUrl = this.getAttribute("data-marker");
    this.markers = [];

    await this.geolocateAndFetch();
  }

  async geolocateAndFetch() {
    try {
      const resp = await fetch("https://hunni-serverless.vercel.app/api/gin-and-juice/geolocation");
      const data = await resp.json();

      if (data.lat && data.lng) {
        this.map.setCenter({ lat: +data.lat, lng: +data.lng });

        if (data.zip) {
          await this.fetchStores(data.zip);
        }
      }
    } catch (e) {}
  }

  async fetchStores(zip) {
    try {
      const resp = await fetch("https://hunni-serverless.vercel.app/api/gin-and-juice/store-locator", {
        method: "POST",
        body: JSON.stringify({ zip, miles: "25" }),
      });
      const data = await resp.json();
      const locations = data.location;

      if (!locations || !locations.length) return;

      var brandPrefix = this.getAttribute("data-brand-prefix");
      var brandNames = this.getAttribute("data-brand-names");
      var filtered;

      if (brandPrefix) {
        filtered = locations.filter(
          (loc) => loc.otherBrand && loc.otherBrand.some((brand) => brand.startsWith(brandPrefix))
        );
      } else if (brandNames) {
        var names = JSON.parse(brandNames);
        filtered = locations.filter(
          (loc) => loc.otherBrand && loc.otherBrand.some((brand) => names.some((n) => brand.indexOf(n) !== -1))
        );
      } else {
        filtered = locations;
      }

      if (!filtered.length) return;

      this.updateSummary(filtered.length, zip);
      this.addMarkers(filtered);
      this.showNearest(filtered[0]);
      this.fitToMarkers();
    } catch (e) {}
  }

  updateSummary(count, zip) {
    if (!this.wrapper) return;
    var summary = this.wrapper.querySelector("[data-map-summary]");
    var link = this.wrapper.querySelector("[data-map-link]");
    if (summary) {
      summary.textContent = count + (count === 1 ? " store" : " stores") + " within 25 miles of " + zip;
    }
    if (link) {
      var url = new URL(link.href, window.location.origin);
      url.searchParams.set("zip", zip);
      url.searchParams.set("miles", "25");
      link.href = url.pathname + url.search;
    }
  }

  decode(value) {
    const doc = new DOMParser().parseFromString(String(value || ""), "text/html");
    return doc.documentElement.textContent;
  }

  mix(hex, target, amount) {
    const parse = (value) => value.replace("#", "").match(/.{2}/g).map((part) => parseInt(part, 16));
    const a = parse(hex);
    const b = parse(target);
    return "#" + a.map((channel, i) => Math.round(channel + (b[i] - channel) * amount).toString(16).padStart(2, "0")).join("");
  }

  palette(land) {
    const ink = this.mix(land, "#000000", 0.62);
    return {
      land: land,
      text: ink,
      stroke: this.mix(land, "#ffffff", 0.3),
      parcel: this.mix(land, ink, 0.25),
      road: this.mix(land, "#ffffff", 0.45),
      arterial: this.mix(land, ink, 0.8),
      water: this.mix(land, ink, 0.12),
      waterText: this.mix(land, ink, 0.5),
    };
  }

  lightStyles(tan) {
    const c = this.mapColor && /^#[0-9a-f]{6}$/i.test(this.mapColor)
      ? this.palette(this.mapColor)
      : tan
      ? { land: "#ebdacc", text: "#84614e", stroke: "#f1e5db", parcel: "#d4baa8", road: "#f6ece4", arterial: "#94715f", water: "#dcc3b2", waterText: "#a48471" }
      : { land: "#eeeeee", text: "#6f6f6f", stroke: "#f5f5f5", parcel: "#bdbdbd", road: "#ffffff", arterial: "#757575", water: "#dadada", waterText: "#9e9e9e" };
    return [
      { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
      { elementType: "geometry", stylers: [{ color: c.land }] },
      { elementType: "labels.text.fill", stylers: [{ color: c.text }] },
      { elementType: "labels.text.stroke", stylers: [{ color: c.stroke }] },
      { featureType: "administrative.land_parcel", elementType: "labels.text.fill", stylers: [{ color: c.parcel }] },
      { featureType: "poi", stylers: [{ visibility: "off" }] },
      { featureType: "road", elementType: "geometry", stylers: [{ color: c.road }] },
      { featureType: "road", elementType: "labels", stylers: [{ visibility: "off" }] },
      { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: c.arterial }] },
      { featureType: "road.highway", elementType: "geometry", stylers: [{ color: c.road }] },
      { featureType: "road.highway", elementType: "labels", stylers: [{ visibility: "off" }] },
      { featureType: "transit", stylers: [{ visibility: "off" }] },
      { featureType: "water", elementType: "geometry", stylers: [{ color: c.water }] },
      { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: c.waterText }] },
    ];
  }

  darkStyles() {
    return [
      { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
      { elementType: "geometry", stylers: [{ color: "#1d1d1d" }] },
      { elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
      { elementType: "labels.text.stroke", stylers: [{ color: "#141414" }] },
      { featureType: "administrative.land_parcel", elementType: "labels", stylers: [{ visibility: "off" }] },
      { featureType: "poi", stylers: [{ visibility: "off" }] },
      { featureType: "road", elementType: "geometry", stylers: [{ color: "#2c2c2c" }] },
      { featureType: "road", elementType: "labels", stylers: [{ visibility: "off" }] },
      { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#363636" }] },
      { featureType: "transit", stylers: [{ visibility: "off" }] },
      { featureType: "water", elementType: "geometry", stylers: [{ color: "#0c0c0c" }] },
    ];
  }

  addMarkers(locations) {
    locations.forEach((location, index) => {
      const icon = this.markerUrl
        ? {
            url: this.markerUrl,
            scaledSize: new google.maps.Size(51, 51),
            anchor: new google.maps.Point(25.5, 25.5),
          }
        : {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 9,
            fillColor: this.dark ? "#C09F5B" : "#000000",
            fillOpacity: 1,
            strokeColor: this.dark ? "#141414" : "#FFFFFF",
            strokeWeight: 2,
          };
      const marker = new google.maps.Marker({
        icon: icon,
        position: { lat: +location.lat, lng: +location.long },
        map: this.map,
        zIndex: locations.length - index,
      });

      marker.addListener("click", () => {
        this.showCard(marker, location);
      });

      this.markers.push(marker);
    });
  }

  fitToMarkers() {
    if (!this.markers.length) return;
    var bounds = new google.maps.LatLngBounds();
    this.markers.forEach(function (marker) { bounds.extend(marker.getPosition()); });
    var wide = window.innerWidth >= 1024;
    var cardTop = 60;
    if (!wide && this.card && !this.card.hidden) {
      cardTop = this.card.offsetTop + this.card.offsetHeight + 36;
    }
    this.map.fitBounds(bounds, { top: cardTop, right: 40, bottom: 40, left: wide ? 360 : 40 });
    var map = this.map;
    var self = this;
    google.maps.event.addListenerOnce(map, "bounds_changed", function () {
      if (map.getZoom() > 14) map.setZoom(14);
      google.maps.event.addListenerOnce(map, "idle", function () {
        self.baseZoom = map.getZoom();
      });
    });
    if (!this.touchListener && window.matchMedia("(hover: none)").matches) {
      this.touchListener = map.addListener("zoom_changed", function () {
        if (!self.userZoomed || self.baseZoom == null) return;
        map.setOptions({ gestureHandling: map.getZoom() > self.baseZoom ? "greedy" : "none" });
      });
    }
  }

  showNearest(location) {
    if (this.markers.length) {
      this.showCard(this.markers[0], location);
    }
  }

  capitalizeFirstLetters(str) {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((w) => (w ? w[0].toUpperCase() + w.substr(1) : ""))
      .join(" ");
  }

  showCard(marker, location) {
    if (!this.card) return;
    const mapsQuery = encodeURIComponent(
      location.street + ", " + location.city + ", " + location.state + " " + location.zip
    );
    this.markers.forEach((m) => m.setZIndex(m === marker ? 1000 : 1));

    this.card.querySelector("[data-card-name]").textContent = this.decode(this.capitalizeFirstLetters(location.dba));
    this.card.querySelector("[data-card-address]").textContent = this.decode(
      this.capitalizeFirstLetters(location.street) + ", " + this.capitalizeFirstLetters(location.city) + ", " + location.state
    );
    this.card.querySelector("[data-card-distance]").textContent = location.distance + " mi away";
    this.card.querySelector("[data-card-directions]").href =
      "https://www.google.com/maps/dir/?api=1&destination=" + mapsQuery;
    this.card.hidden = false;
  }
}

customElements.define("product-mini-map", ProductMiniMap);
